import { PatientFlag } from '@seen/core/models/flags/patientFlag'
import * as patients from '@seen/core/models/patients'
import { createReferenceStringWithHistory } from '@seen/core/references'
import { groupBy, orderBy } from 'lodash-es'
import type { ReactNode } from 'react'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import { formatIsolationRequiredSetOn } from '#src/components/isolation-required/isolationRequiredFlag.ts'
import { versionsForOosaHistory } from '#src/components/oosa/UseOosaFlagActivity.tsx'
import { useBulkResourceVersions } from '#src/hooks/useResourceVersions.ts'

export function useIsolationRequiredFlagActivity(
  flagIds: readonly string[] | undefined,
  fallbackFlags: readonly PatientFlag[] = []
): ReactNode[] {
  const uniqueIds = [...new Set(flagIds ?? [])].filter(Boolean)
  const { data: historyVersions = [] } = useBulkResourceVersions(
    PatientFlag,
    uniqueIds
  )

  const historyById = groupBy(
    historyVersions,
    (version) => version.toFhirResource().id ?? ''
  )
  const fallbackById = groupBy(
    fallbackFlags,
    (flag) => flag.toFhirResource().id ?? ''
  )
  const described = [...new Set([...uniqueIds, ...Object.keys(fallbackById)])]
    .filter(Boolean)
    .flatMap((flagId) => {
      const fallback = fallbackById[flagId]?.[0]
      return describeIsolationRequiredFlagVersions(
        versionsForOosaHistory(historyById[flagId], fallback)
      )
    })

  return orderBy(
    described,
    [(entry) => versionTime(entry.version)],
    ['desc']
  ).map(({ version, previous, action }) => {
    const fhir = version.toFhirResource()
    return (
      <ActivityEntry
        key={
          fhir.meta?.versionId
            ? createReferenceStringWithHistory(fhir)
            : (fhir.id ?? action)
        }
        author={version.resourceAuthor}
        time={versionTime(version) || undefined}
        action={action}
      >
        {version.oosaNotes && version.oosaNotes !== previous?.oosaNotes && (
          <div className="rounded-md bg-muted px-2 py-1 text-xs whitespace-pre-wrap text-foreground-secondary">
            {version.oosaNotes}
          </div>
        )}
      </ActivityEntry>
    )
  })
}

interface DescribedIsolationRequiredFlagVersion {
  version: PatientFlag
  previous: PatientFlag | undefined
  action: string
}

function describeIsolationRequiredFlagVersions(
  versions: readonly PatientFlag[]
): DescribedIsolationRequiredFlagVersion[] {
  const ascending = orderBy(
    versions,
    [(version) => versionTime(version)],
    ['asc']
  )
  const entries: DescribedIsolationRequiredFlagVersion[] = []
  ascending.forEach((version, index) => {
    const previous = index > 0 ? ascending[index - 1] : undefined
    const action = describeIsolationRequiredFlagChange(previous, version)
    if (!action) {
      return
    }
    entries.push({ version, previous, action })
  })
  return entries
}

export function describeIsolationRequiredFlagChange(
  previous: PatientFlag | undefined,
  current: PatientFlag
): string | undefined {
  if (!previous) {
    const setOn = current.isolationRequiredDetails?.setOn ?? current.start
    return setOn
      ? `created the Isolation Required flag (${formatIsolationRequiredSetOn(setOn)})`
      : 'created the Isolation Required flag'
  }

  if (previous.status !== current.status) {
    if (current.status === 'inactive') {
      return 'inactivated the Isolation Required flag'
    }
    if (current.status === 'active') {
      return 'reactivated the Isolation Required flag'
    }
  }

  if (previous.reason !== current.reason) {
    const reason =
      current.isolationRequiredDetails != null
        ? patients.formatIsolationRequiredReasonDisplay(
            current.isolationRequiredDetails.reason
          )
        : current.reason
    return reason ? `updated the reason to ${reason}` : 'cleared the reason'
  }

  if (previous.oosaNotes !== current.oosaNotes) {
    return current.oosaNotes ? 'added a note' : 'cleared the note'
  }

  return undefined
}

function versionTime(flag: PatientFlag): string {
  return flag.toFhirResource().meta?.lastUpdated ?? ''
}
